import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Stack,
  IconButton,
  Avatar,
  Divider
} from '@mui/joy';
import { ExpandMore, ExpandLess, FilterList } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { PreferenceService } from '@/services/preferenceService';
import {
  EmployeePreferences,
  PreferenceSummary
} from '@/types/preference';
import { userService } from '@/services/userService';
import { User } from '@/types/user';
import PreferencesFilters, { PreferencesFilters as PreferencesFiltersType } from '@/components/PreferencesFilters';

interface UserPreferences {
  employee_id: number;
  preferences: EmployeePreferences;
  user?: User;
}

const AllPreferencesPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [userPreferences, setUserPreferences] = useState<UserPreferences[]>([]);
  const [allEmployees, setAllEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<PreferencesFiltersType>({
    userId: null,
    preferenceCategory: 'all',
    preferenceType: 'all',
    showInactiveUsersOnly: false,
  });

  useEffect(() => {
    loadAllUserPreferences();
    loadAllEmployees();
  }, []);

  const loadAllUserPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all user preferences
      const allPreferences = await PreferenceService.getAllUserPreferences();
      
      // Get user details for each employee - with better error handling
      const preferencesWithUsers = await Promise.all(
        allPreferences.map(async (item) => {
          try {
            const user = await userService.getUserById(item.employee_id);
            return { ...item, user };
          } catch (err) {
            console.error(`Error fetching user ${item.employee_id}:`, err);
            return { ...item, user: undefined };
          }
        })
      );
      
      setUserPreferences(preferencesWithUsers);
    } catch (err) {
      console.error('Error loading all user preferences:', err);
      setError('Failed to load user preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAllEmployees = async () => {
    try {
      const employees = await userService.getEmployees();
      setAllEmployees(employees);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const calculateUserSummary = (preferences: EmployeePreferences): PreferenceSummary => {
    return PreferenceService.calculatePreferenceSummary(preferences);
  };

  const getTotalPreferences = (preferences: EmployeePreferences): number => {
    return (
      preferences.date_preferences.length +
      preferences.day_preferences.length +
      preferences.shift_type_preferences.length
    );
  };

  const toggleExpanded = (employeeId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedItems(newExpanded);
  };

  const getInitials = (user: User | undefined): string => {
    if (!user) return 'U';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserDisplayName = (user: User | undefined, employeeId: number): string => {
    if (!user) return `Employee ${employeeId}`;
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim() || `Employee ${employeeId}`;
  };

  // Filter user preferences based on current filters
  const filteredUserPreferences = useMemo(() => {
    return userPreferences.filter((item) => {
      // User filter
      if (filters.userId && item.employee_id !== filters.userId) {
        return false;
      }

      // Hide inactive users when switch is off, show them when switch is on
      if (!filters.showInactiveUsersOnly) {
        // When switch is off, hide inactive users
        if (item.user?.is_active === false) {
          return false;
        }
      }

      // Preference category filter
      if (filters.preferenceCategory !== 'all') {
        let hasMatchingCategory = false;
        switch (filters.preferenceCategory) {
          case 'date':
            hasMatchingCategory = item.preferences.date_preferences.length > 0;
            break;
          case 'day':
            hasMatchingCategory = item.preferences.day_preferences.length > 0;
            break;
          case 'shift_type':
            hasMatchingCategory = item.preferences.shift_type_preferences.length > 0;
            break;
        }
        if (!hasMatchingCategory) {
          return false;
        }
      }

      // Preference type filter
      if (filters.preferenceType !== 'all') {
        let hasMatchingType = false;
        
        // Check date preferences
        if (filters.preferenceType === 'unavailable' || filters.preferenceType === 'preferred') {
          hasMatchingType = item.preferences.date_preferences.some(
            pref => pref.preference_type === filters.preferenceType
          ) || item.preferences.day_preferences.some(
            pref => pref.preference_type === filters.preferenceType
          );
        }
        
        // Check shift type preferences
        if (filters.preferenceType === 'avoid') {
          hasMatchingType = item.preferences.shift_type_preferences.some(
            pref => pref.preference_type === 'avoid'
          );
        } else if (filters.preferenceType === 'preferred') {
          hasMatchingType = hasMatchingType || item.preferences.shift_type_preferences.some(
            pref => pref.preference_type === 'preferred'
          );
        }
        
        if (!hasMatchingType) {
          return false;
        }
      }

      return true;
    });
  }, [userPreferences, filters]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadAllUserPreferences} variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  if (userPreferences.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="h4" sx={{ mb: 2 }}>
          All User Preferences
        </Typography>
        <Alert color="warning">
          No user preferences found. Employees can set their preferences in their profile.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography level="h3">
          All User Preferences
        </Typography>
        <Button
          startDecorator={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          size="sm"
          variant="outlined"
          color={showFilters ? "primary" : "neutral"}
        >
          Filters
        </Button>
      </Stack>

      {/* Filters Section */}
      {showFilters && (
        <Box sx={{ mb: 3 }}>
          <PreferencesFilters
            filters={filters}
            onFiltersChange={setFilters}
            users={allEmployees}
            onClose={() => setShowFilters(false)}
          />
        </Box>
      )}

      {/* Filter Summary */}
      {showFilters && (filters.userId || filters.preferenceCategory !== 'all' || filters.preferenceType !== 'all' || filters.showInactiveUsersOnly) && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography level="body-sm" color="neutral">
              Showing {filteredUserPreferences.length} of {userPreferences.length} users
            </Typography>
            {filters.userId && (
              <Chip size="sm" variant="soft" color="primary">
                {allEmployees.find(u => u.id === filters.userId)?.first_name} {allEmployees.find(u => u.id === filters.userId)?.last_name}
              </Chip>
            )}
            {filters.preferenceCategory !== 'all' && (
              <Chip size="sm" variant="soft" color="primary">
                {filters.preferenceCategory === 'date' ? 'Date Preferences' : 
                 filters.preferenceCategory === 'day' ? 'Day Preferences' : 
                 'Shift Type Preferences'}
              </Chip>
            )}
            {filters.preferenceType !== 'all' && (
              <Chip size="sm" variant="soft" color="primary">
                {filters.preferenceType}
              </Chip>
            )}
            {filters.showInactiveUsersOnly && (
              <Chip size="sm" variant="soft" color="danger">
                Showing Inactive Users
              </Chip>
            )}
          </Stack>
        </Box>
      )}

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="h4" color="primary">
                {filteredUserPreferences.length}
              </Typography>
              <Typography level="body-sm" color="neutral">
                Users with Preferences
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="h4" color="success">
                {filteredUserPreferences.reduce((total, item) => 
                  total + getTotalPreferences(item.preferences), 0
                )}
              </Typography>
              <Typography level="body-sm" color="neutral">
                Total Preferences
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography level="h4" color="warning">
                {filteredUserPreferences.reduce((total, item) => 
                  total + item.preferences.date_preferences.length, 0
                )}
              </Typography>
              <Typography level="body-sm" color="neutral">
                Date Preferences
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Preferences List */}
      {filteredUserPreferences.length === 0 ? (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <Typography level="h4" color="neutral">
                {userPreferences.length === 0 ? 'No user preferences found' : 'No users match your filters'}
              </Typography>
              <Typography level="body-sm" color="neutral" textAlign="center">
                {userPreferences.length === 0
                  ? 'Employees can set their preferences in their profile.'
                  : 'Try adjusting your filters to see more results.'
                }
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filteredUserPreferences.map((item) => {
          const summary = calculateUserSummary(item.preferences);
          const totalPreferences = getTotalPreferences(item.preferences);
          const isExpanded = expandedItems.has(item.employee_id);
          const isInactiveUser = item.user?.is_active === false;
          
          return (
            <Card 
              key={item.employee_id}
              variant={isInactiveUser ? "outlined" : undefined}
              color={isInactiveUser ? "danger" : undefined}
              sx={isInactiveUser ? {
                borderWidth: 2,
                borderColor: 'var(--joy-palette-danger-500)'
              } : undefined}
            >
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar sx={{ mr: 2 }}>
                    {getInitials(item.user)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography level="h4" fontWeight="md">
                      {getUserDisplayName(item.user, item.employee_id)}
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      {item.user?.email || 'No email available'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="sm" color="primary">
                      {totalPreferences} preferences
                    </Chip>
                    {summary.date_preferences > 0 && (
                      <Chip size="sm" color="success" variant="soft">
                        {summary.date_preferences} dates
                      </Chip>
                    )}
                    <IconButton
                      size="sm"
                      onClick={() => toggleExpanded(item.employee_id)}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Expandable Content */}
                {isExpanded && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Preference Details */}
                    <Grid container spacing={2}>
                      {/* Date Preferences */}
                      {item.preferences.date_preferences.length > 0 && (
                        <Grid xs={12} md={6}>
                          <Typography level="h4" fontWeight="md" sx={{ mb: 1 }}>
                            Date Preferences ({item.preferences.date_preferences.length})
                          </Typography>
                          <Stack spacing={1}>
                            {item.preferences.date_preferences.slice(0, 3).map((pref) => (
                              <Chip
                                key={pref.id}
                                size="sm"
                                color={pref.preference_type === 'unavailable' ? 'danger' : 'success'}
                                variant="soft"
                              >
                                {new Date(pref.preference_date).toLocaleDateString()} - {pref.preference_type}
                              </Chip>
                            ))}
                            {item.preferences.date_preferences.length > 3 && (
                              <Typography level="body-sm" color="neutral">
                                +{item.preferences.date_preferences.length - 3} more
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      )}

                      {/* Day Preferences */}
                      {item.preferences.day_preferences.length > 0 && (
                        <Grid xs={12} md={6}>
                          <Typography level="h4" fontWeight="md" sx={{ mb: 1 }}>
                            Day Preferences ({item.preferences.day_preferences.length})
                          </Typography>
                          <Stack spacing={1}>
                            {item.preferences.day_preferences.slice(0, 3).map((pref) => (
                              <Chip
                                key={pref.id}
                                size="sm"
                                color={pref.preference_type === 'unavailable' ? 'danger' : 'success'}
                                variant="soft"
                              >
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][pref.day_of_week]} - {pref.preference_type}
                              </Chip>
                            ))}
                            {item.preferences.day_preferences.length > 3 && (
                              <Typography level="body-sm" color="neutral">
                                +{item.preferences.day_preferences.length - 3} more
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      )}

                      {/* Shift Type Preferences */}
                      {item.preferences.shift_type_preferences.length > 0 && (
                        <Grid xs={12} md={6}>
                          <Typography level="h4" fontWeight="md" sx={{ mb: 1 }}>
                            Shift Type Preferences ({item.preferences.shift_type_preferences.length})
                          </Typography>
                          <Stack spacing={1}>
                            {item.preferences.shift_type_preferences.slice(0, 3).map((pref) => (
                              <Chip
                                key={pref.id}
                                size="sm"
                                color={pref.preference_type === 'avoid' ? 'danger' : 'success'}
                                variant="soft"
                              >
                                Shift {pref.shift_type_id} - {pref.preference_type}
                              </Chip>
                            ))}
                            {item.preferences.shift_type_preferences.length > 3 && (
                              <Typography level="body-sm" color="neutral">
                                +{item.preferences.shift_type_preferences.length - 3} more
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>

                    {totalPreferences === 0 && (
                      <Alert color="warning" sx={{ mt: 2 }}>
                        No preferences set
                      </Alert>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
        </Stack>
      )}

      {/* Help Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography level="h4" fontWeight="md" sx={{ mb: 2 }}>
            Manager View
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • This view shows all employee preferences to help with schedule planning
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • Use this information when creating schedules to accommodate employee preferences
          </Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            • Consider preferences when assigning shifts to improve employee satisfaction
          </Typography>
          <Typography level="body-sm" sx={{ mt: 2 }}>
            Employees can set their preferences in their profile, and you'll see them here for planning purposes.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllPreferencesPage;